import BaseLayout from '@/layout/BaseLayout';
import type { FacilityDto } from '@shelflix/trpc/routes/facility/list/useCase';
import { ConfirmationModal } from '@shelflix/ui-web/components/ConfirmationModal';
import React, { useEffect, useRef, useState } from 'react';
import Icon from '@shelflix/ui-web/components/Icon';
import { GenericTable } from '@shelflix/ui-web/components/GenericTable';
import { Button } from '@shelflix/ui-web/components/ui/button';
import { trpc } from '@/trpcClient';
import { useMutation, useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useLocation, useNavigate } from 'react-router-dom';
import type { ListFacilityFilterTypeWithValues } from '@shelflix/schemas/facility/list';
import { FacilityFilter } from './filter';
import { useHasScopes } from '@/hooks/useHasScopes';

export function FacilityList() {
  const [filters, setFilters] = useState<ListFacilityFilterTypeWithValues>({});
  const location = useLocation();
  const navigate = useNavigate();
  const hasShownToast = useRef(false);
  const [page, setPage] = useState(1);
  const [openModal, setOpenModal] = useState(false);
  const pageSize = 10;

  const canEditFacility = useHasScopes(['admin:facilities:edit']);
  const canDeleteFacility = useHasScopes(['admin:facilities:delete']);

  const [selectedUuid, setSelectedUuid] = useState<string | null>(null);
  const deleteFacilityMutation = useMutation(trpc.facility.delete.mutationOptions());

  const { data, isLoading, isError, refetch } = useQuery(
    trpc.facility.list.queryOptions({
      page,
      recordsPerPage: pageSize,
      ...(filters.search?.value && { search: { ...filters.search } }),
      ...(filters.filter && { filter: filters.filter.map((f) => ({ ...f.field })) }),
      ...(filters.sort && { sort: filters.sort }),
    })
  );
  const facilities = data?.facilities ?? [];
  const totalItems = data?.total ?? 0;

  const columns: Array<{
    header: string;
    key: keyof FacilityDto;
    render?: (value: FacilityDto[keyof FacilityDto], row: FacilityDto) => React.ReactNode;
  }> = [
    {
      header: 'Nome do estabelecimento',
      key: 'name',
      render: (_, row) => (
        <div className="flex flex-row items-center gap-2">
          {/* "https://t.ctcdn.com.br/d3zdfcWo-gT6PFFSh1Y9LOeE2kk=/1200x675/smart/i257652.jpeg" */}
          <img src={row.logoUrl} alt={row.name} className="h-8 w-8 rounded object-cover" />
          <span
            className="[font-family:'Figtree',Helvetica] text-base font-medium text-[var(--gray-primary)]"
            style={{ wordBreak: 'break-word', whiteSpace: 'normal' }}
          >
            {row.name}
          </span>
        </div>
      ),
    },
    {
      header: 'Endereço',
      key: 'address',
      render: (_, row) => (
        <div className="flex flex-col">
          <span className="[font-family:'Figtree',Helvetica] text-base font-medium text-[var(--gray-primary)]">
            {`${row.address}, nº ${row.number}, ${row.district}, ${row.city.name}, ${row.state.name}`}
          </span>
        </div>
      ),
    },
    {
      header: 'CNPJ',
      key: 'cnpj',
      render: (_, row) => (
        <div className="[font-family:'Figtree'] text-base text-[var(--gray-primary)]">{row.cnpj}</div>
      ),
    },
    {
      header: 'Ações',
      key: 'uuid',
      render: (_, row) => (
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            className={`h-8 w-8 rounded-md p-0 ${canEditFacility ? 'text-primary' : 'cursor-not-allowed text-[var(--gray-secondary)]'}`}
            onClick={(e) => canEditFacility && handleEdit(e, row.uuid)}
            disabled={!canEditFacility}
          >
            <Icon name="edit" fill={1} />
          </Button>
          <Button variant="ghost" className="text-primary h-8 w-8 rounded-md p-0" onClick={(_) => navigate(row.uuid)}>
            <Icon name="featured_play_list" />
          </Button>
          <Button
            variant="ghost"
            onClick={(e) => canDeleteFacility && showModal(e, row.uuid)}
            className={`h-8 w-8 rounded-md p-0 ${canDeleteFacility ? 'text-primary' : 'cursor-not-allowed text-[var(--gray-secondary)]'}`}
            disabled={!canDeleteFacility}
          >
            <Icon name="delete" />
          </Button>
        </div>
      ),
    },
  ];

  const handleEdit = (e: React.MouseEvent<HTMLButtonElement>, uuid: string) => {
    e.stopPropagation();
    navigate(`edit/${uuid}`);
  };

  const showModal = (e: React.MouseEvent<HTMLButtonElement>, uuid: string) => {
    e.stopPropagation();
    setSelectedUuid(uuid);
    setOpenModal(true);
  };

  const deleteFacility = async () => {
    if (!selectedUuid) return;

    deleteFacilityMutation.mutate(
      { uuid: selectedUuid },
      {
        onSuccess: (_result) => {
          toast.success('Estabelecimento removido com sucesso');
        },
        onError: (error) => {
          toast.error(`Erro ao remover estabelecimento: ${error.message}`);
        },
      }
    );
    setOpenModal(false);
    setSelectedUuid(null);
    await new Promise((r) => setTimeout(r, 300));
    await refetch();
  };

  useEffect(() => {
    if (location.state?.toastSuccess && !hasShownToast.current) {
      toast.success('Alterações salvas com sucesso');
      hasShownToast.current = true;

      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, navigate]);

  return (
    <BaseLayout>
      <section className="flex flex-col gap-8">
        {/* <RestrictedComponent redirectToLogin={true} requiredScopes={['facility:list']}> */}
        <FacilityFilter
          onChange={(filters) => {
            setFilters(filters);
            setPage(1);
          }}
        />
        {/* </RestrictedComponent> */}
        {isLoading && <p className="p-4">Carregando estabelecimentos...</p>}
        {isError && <p className="p-4 text-red-500">Erro ao carregar estabelecimentos.</p>}
        {!isLoading && !isError && (
          <GenericTable
            columns={columns}
            data={facilities}
            onPageChange={setPage}
            itemsPerPages={pageSize}
            totalItems={totalItems}
            page={page}
          />
        )}
      </section>
      <ConfirmationModal
        title="Deletar estabelecimento"
        description="Tem certeza?"
        open={openModal}
        onCancel={() => setOpenModal(false)}
        onConfirm={() => deleteFacility()}
        confirmButtonVariant="destructive"
        loading={deleteFacilityMutation.isPending}
      />
    </BaseLayout>
  );
}
